// frontend/src/App.js
import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Box, CircularProgress } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';
import { OrganizationProvider } from './contexts/OrganizationContext';
import ErrorBoundary from './components/common/ErrorBoundary';

// Session Management
import SessionTimeoutWarning from './components/auth/SessionTimeoutWarning';

// Auth Components (eager - needed on first load)
import AuthWrapper from './components/auth/AuthWrapper';
import MainLayout from './layouts/MainLayout';
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Unauthorized from './pages/Unauthorized';

// Lazy-loaded auth pages
const VerifyEmail = React.lazy(() => import('./pages/auth/VerifyEmail'));
const VerifyEmailSent = React.lazy(() => import('./pages/auth/VerifyEmailSent'));
const ResetPassword = React.lazy(() => import('./pages/auth/ResetPassword'));
const AcceptInvite = React.lazy(() => import('./pages/auth/AcceptInvite'));

// Lazy-loaded pages
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Profile = React.lazy(() => import('./pages/Profile'));
const TextTranslation = React.lazy(() => import('./pages/TextTranslation'));
const XliffTranslation = React.lazy(() => import('./pages/XliffTranslation'));

// Projects (lazy)
const ProjectsList = React.lazy(() => import('./components/projects/ProjectsList'));
const CreateEditProject = React.lazy(() => import('./components/projects/CreateEditProject'));
const ProjectDetails = React.lazy(() => import('./components/projects/ProjectDetails'));
const TranslationReview = React.lazy(() => import('./components/translation/TranslationReview'));

// Translation Memories & Glossaries (lazy)
const TranslationMemoriesList = React.lazy(() => import('./components/translationMemories/TranslationMemoriesList'));
const GlossariesList = React.lazy(() => import('./components/glossaries/GlossariesList'));
const GlossaryDetails = React.lazy(() => import('./components/glossaries/GlossaryDetails'));
const CreateEditGlossary = React.lazy(() => import('./components/glossaries/CreateEditGlossary'));

// Analytics (lazy)
const Analytics = React.lazy(() => import('./pages/analytics/Analytics'));
const CostAttribution = React.lazy(() => import('./pages/analytics/CostAttribution'));
const ProjectAnalytics = React.lazy(() => import('./pages/analytics/ProjectAnalytics'));
const ProductivityReport = React.lazy(() => import('./pages/analytics/ProductivityReport'));
const LanguageStats = React.lazy(() => import('./pages/analytics/LanguageStats'));
const CostAnalysis = React.lazy(() => import('./pages/analytics/CostAnalysis'));
const QualityAnalytics = React.lazy(() => import('./pages/analytics/QualityAnalytics'));
const ConsistencyAnalytics = React.lazy(() => import('./pages/analytics/ConsistencyAnalytics'));
const BatchAnalytics = React.lazy(() => import('./pages/analytics/BatchAnalytics'));
const WorkflowAnalytics = React.lazy(() => import('./pages/analytics/WorkflowAnalytics'));
const PipelineHealth = React.lazy(() => import('./pages/analytics/PipelineHealth'));
const PipelineAdmin = React.lazy(() => import('./pages/settings/PipelineAdmin'));

// Settings (lazy)
const MTConfigurations = React.lazy(() => import('./pages/settings/MTConfigurations'));
const BillingPage = React.lazy(() => import('./pages/settings/BillingPage'));
const PayosReturnPage = React.lazy(() => import('./pages/billing/PayosReturnPage'));
const PayosCancelPage = React.lazy(() => import('./pages/billing/PayosCancelPage'));
const QAProfiles = React.lazy(() => import('./pages/settings/QAProfiles'));
const ApiKeysManagement = React.lazy(() => import('./pages/settings/ApiKeysManagement'));
const BrandingSettings = React.lazy(() => import('./pages/settings/BrandingSettings'));
const EmailTemplatesSettings = React.lazy(() => import('./pages/settings/EmailTemplatesSettings'));
const AdminMTUsageManagement = React.lazy(() => import('./components/admin/AdminMTUsageManagement'));
const LLMConfigurations = React.lazy(() => import('./pages/LLMConfigurations'));
const LLMPromptTemplates = React.lazy(() => import('./pages/LLMPromptTemplates'));
const AutoApprovalRules = React.lazy(() => import('./pages/AutoApprovalRules'));
// Wave-03 · Semantic TM preview & compare
const SemanticTMPreview = React.lazy(() => import('./pages/SemanticTMPreview'));
// Wave-04 · Admin-managed domain / industry catalog
const DomainsAdmin = React.lazy(() => import('./pages/DomainsAdmin'));
// Wave-05 · Audit log dashboard
const AuditLogAdmin = React.lazy(() => import('./pages/AuditLogAdmin'));
// Wave-05 · PII scan dashboard
const PIIScanAdmin = React.lazy(() => import('./pages/PIIScanAdmin'));
// Wave-05 · SSO admin + callback
const SSOConfigAdmin = React.lazy(() => import('./pages/SSOConfigAdmin'));
// Wave-06 · Cross-project reviewer capacity dashboard
const ReviewerCapacityAdmin = React.lazy(() => import('./pages/ReviewerCapacityAdmin'));
// Wave-07 · Global command palette (Ctrl/Cmd+K)
const CommandPalette = React.lazy(() => import('./components/common/CommandPalette'));
// Wave-07 · Reviewer smart queue (cross-project)
const ReviewQueue = React.lazy(() => import('./pages/ReviewQueue'));
// Wave-09 · Item 2 · GDPR admin (export + erase)
const GdprAdmin = React.lazy(() => import('./pages/GdprAdmin'));
// Wave-09 · Per-org TM leverage weights (memoQ-style analysis)
const TmLeverageWeightsAdmin = React.lazy(() => import('./pages/TmLeverageWeightsAdmin'));
const SSOCallback = React.lazy(() => import('./pages/auth/SSOCallback'));
const FormatTemplatesPage = React.lazy(() => import('./pages/FormatTemplatesPage'));

// Organizations (lazy)
const OrganizationSettings = React.lazy(() => import('./pages/organizations').then(m => ({ default: m.OrganizationSettings })));
const CreateOrganization = React.lazy(() => import('./pages/organizations').then(m => ({ default: m.CreateOrganization })));
const UpgradePlan = React.lazy(() => import('./pages/organizations').then(m => ({ default: m.UpgradePlan })));

// Checkout (lazy)
const PaddleCheckout = React.lazy(() => import('./pages/checkout/PaddleCheckout'));

// Platform Administration (lazy)
const PlatformDashboard = React.lazy(() => import('./pages/platform').then(m => ({ default: m.PlatformDashboard })));
const MTEnginesManagement = React.lazy(() => import('./pages/platform').then(m => ({ default: m.MTEnginesManagement })));
const LLMProvidersManagement = React.lazy(() => import('./pages/platform').then(m => ({ default: m.LLMProvidersManagement })));
const OrganizationsManagement = React.lazy(() => import('./pages/platform').then(m => ({ default: m.OrganizationsManagement })));
const RevenueAnalytics = React.lazy(() => import('./pages/platform').then(m => ({ default: m.RevenueAnalytics })));
const UsageMonitoring = React.lazy(() => import('./pages/platform').then(m => ({ default: m.UsageMonitoring })));
const PlatformCosts = React.lazy(() => import('./pages/platform').then(m => ({ default: m.PlatformCosts })));

// Users Management (lazy)
const UsersList = React.lazy(() => import('./pages/users/UsersList'));
const UserDetails = React.lazy(() => import('./pages/users/UserDetails'));
const CreateEditUser = React.lazy(() => import('./pages/users/CreateEditUser'));

// Webhooks (lazy)
const WebhookList = React.lazy(() => import('./pages/webhooks/WebhookList'));
const WebhookForm = React.lazy(() => import('./pages/webhooks/WebhookForm'));
const WebhookDetail = React.lazy(() => import('./pages/webhooks/WebhookDetail'));

// Support Tickets (lazy)
const SupportTicketList = React.lazy(() => import('./pages/support/SupportTicketList'));
const SupportTicketDetail = React.lazy(() => import('./pages/support/SupportTicketDetail'));
const CreateSupportTicket = React.lazy(() => import('./pages/support/CreateSupportTicket'));

// Guest Access (lazy)
const GuestProjectViewer = React.lazy(() => import('./pages/GuestProjectViewer'));
const ClientPortalViewer = React.lazy(() => import('./pages/ClientPortalViewer'));

// Suspense fallback for lazy-loaded routes
const PageLoader = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
    <CircularProgress />
  </Box>
);

// Create a client with enhanced configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false;
        }
        return failureCount < 2;
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    },
    mutations: {
      retry: false,
    },
  },
});

// Create MUI theme with enhanced configuration
const theme = createTheme({
  palette: {
    primary: {
      main: '#2563eb',
      light: '#60a5fa',
      dark: '#1d4ed8',
      50: '#eff6ff',
      100: '#dbeafe',
      200: '#bfdbfe',
      300: '#93c5fd',
      400: '#60a5fa',
      500: '#3b82f6',
      600: '#2563eb',
      700: '#1d4ed8',
      800: '#1e40af',
      900: '#1e3a8a',
    },
    secondary: {
      main: '#64748b',
      light: '#94a3b8',
      dark: '#334155',
    },
    success: {
      main: '#22c55e',
      light: '#4ade80',
      dark: '#16a34a',
    },
    error: {
      main: '#ef4444',
      light: '#f87171',
      dark: '#dc2626',
      50: '#fef2f2',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
    info: {
      main: '#3b82f6',
      light: '#60a5fa',
      dark: '#2563eb',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
    divider: '#e2e8f0',
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
    },
    body1: {
      fontSize: '1rem',
    },
    body2: {
      fontSize: '0.875rem',
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
    caption: {
      fontSize: '0.75rem',
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 600,
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          padding: '8px 16px',
          fontWeight: 600,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          color: '#475569',
        },
      },
    },
  },
});

// Wave-07 · Global keyboard listener + palette host. Ctrl/Cmd+K toggles.
// Lives under Router so CommandPalette can useNavigate.
function GlobalCommandPalette() {
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => {
    const handler = (e) => {
      const meta = e.ctrlKey || e.metaKey;
      if (meta && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);
  return (
    <React.Suspense fallback={null}>
      <CommandPalette open={open} onClose={() => setOpen(false)} />
    </React.Suspense>
  );
}

// App Component
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <CssBaseline />
          <ErrorBoundary>
          <Router>
            <AuthProvider>
              <OrganizationProvider>
              <AuthWrapper> {/* ADDED: Wrap everything with AuthWrapper */}
                <div className="min-h-screen bg-gray-50">
                  {/* Global Session Timeout Warning */}
                  <SessionTimeoutWarning />
                  
                  {/* Toast notifications */}
                  <Toaster
                    position="top-right"
                    reverseOrder={false}
                    gutter={8}
                    toastOptions={{
                      duration: 4000,
                      style: {
                        background: '#1e293b',
                        color: '#fff',
                        borderRadius: '8px',
                        padding: '12px 16px',
                        fontSize: '14px',
                      },
                      success: {
                        duration: 3000,
                        iconTheme: {
                          primary: '#22c55e',
                          secondary: '#fff',
                        },
                        style: {
                          background: '#065f46',
                        },
                      },
                      error: {
                        duration: 5000,
                        iconTheme: {
                          primary: '#ef4444',
                          secondary: '#fff',
                        },
                        style: {
                          background: '#991b1b',
                        },
                      },
                      loading: {
                        iconTheme: {
                          primary: '#3b82f6',
                          secondary: '#fff',
                        },
                      },
                    }}
                  />
                  
                  {/* Routes */}
                  <Suspense fallback={<PageLoader />}>
                  <Routes>
                    {/* Public Routes */}
                    <Route path="/login" element={<Login />} />
                    {/* Wave-05 · SSO callback landing (unauthenticated; hydrates token from URL fragment) */}
                    <Route path="/auth/sso/callback" element={<SSOCallback />} />
                    <Route path="/signup" element={<Signup />} />
                    <Route path="/verify-email/:token" element={<VerifyEmail />} />
                    <Route path="/verify-email-sent" element={<VerifyEmailSent />} />
                    <Route path="/reset-password/:token" element={<ResetPassword />} />
                    <Route path="/invite/:token" element={<AcceptInvite />} />
                    <Route path="/unauthorized" element={<Unauthorized />} />
                    <Route path="/guest/:token" element={<GuestProjectViewer />} />
                    <Route path="/portal/:slug" element={<ClientPortalViewer />} />
                    
                    {/* Protected Routes with MainLayout */}
                    <Route
                      path="/"
                      element={
                        <ProtectedRoute>
                          <MainLayout />
                        </ProtectedRoute>
                      }
                    >
                      {/* Default redirect */}
                      <Route index element={<Navigate to="/dashboard" replace />} />
                      
                      {/* Dashboard & Profile */}
                      <Route path="dashboard" element={<Dashboard />} />
                      <Route path="profile" element={<Profile />} />
                      {/* Wave-07 · reviewer smart queue */}
                      <Route path="my-queue" element={<ReviewQueue />} />

                      {/* Text Translation */}
                      <Route path="text-translation" element={<TextTranslation />} />
                      <Route path="xliff-translation" element={<XliffTranslation />} />

                      {/* Projects Routes */}
                      <Route path="projects">
                        <Route index element={<ProjectsList />} />
                        <Route path="create" element={<CreateEditProject />} />
                        <Route path=":id" element={<ProjectDetails />} />
                        <Route path=":id/edit" element={<CreateEditProject />} />
                        <Route path=":projectId/review" element={<TranslationReview />} />
                      </Route>
                      
                      {/* Translation Memories Routes */}
                      <Route path="translation-memories">
                        <Route index element={<TranslationMemoriesList />} />
                      </Route>
                      
                      {/* Glossaries Routes */}
                      <Route path="glossaries">
                        <Route index element={<GlossariesList />} />
                        <Route path="create" element={<CreateEditGlossary />} />
                        <Route path=":id" element={<GlossaryDetails />} />
                        <Route path=":id/edit" element={<CreateEditGlossary />} />
                      </Route>
                      
                      {/* Analytics Routes - Admin and Project Manager Only */}
                      <Route
                        path="analytics"
                        element={
                          <ProtectedRoute requiredRole="project_manager">
                            <Outlet />
                          </ProtectedRoute>
                        }
                      >
                        <Route index element={<Analytics />} />
                        <Route path="projects/:projectId" element={<ProjectAnalytics />} />
                        <Route path="productivity" element={<ProductivityReport />} />
                        <Route path="languages" element={<LanguageStats />} />
                        <Route path="costs" element={<CostAnalysis />} />
                        <Route path="cost-attribution" element={
                          <ProtectedRoute requiredRole="admin"><CostAttribution /></ProtectedRoute>
                        } />
                        <Route path="quality" element={<QualityAnalytics />} />
                        <Route path="consistency" element={<ConsistencyAnalytics />} />
                        <Route path="batching" element={<BatchAnalytics />} />
                        <Route path="workflow" element={<WorkflowAnalytics />} />
                        <Route path="pipeline-health" element={<PipelineHealth />} />
                      </Route>

                      {/* Users Management Routes */}
                      <Route 
                        path="users" 
                        element={
                          <ProtectedRoute requiredRole="project_manager">
                            <UsersList />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="users/create" 
                        element={
                          <ProtectedRoute requiredRole="project_manager">
                            <CreateEditUser />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="users/:id" 
                        element={
                          <ProtectedRoute requiredRole="project_manager">
                            <UserDetails />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="users/:id/edit" 
                        element={
                          <ProtectedRoute requiredRole="project_manager">
                            <CreateEditUser />
                          </ProtectedRoute>
                        } 
                      />
                      
                      {/* Checkout Routes */}
                      <Route path="checkout/paddle" element={<PaddleCheckout />} />

                      {/* Organization Routes */}
                      <Route path="organizations">
                        <Route path="settings" element={<OrganizationSettings />} />
                        <Route path="new" element={<CreateOrganization />} />
                        <Route path="usage" element={<OrganizationSettings />} />
                        <Route path="upgrade" element={<UpgradePlan />} />
                        <Route path="billing" element={<BillingPage />} />
                      </Route>

                      {/* PayOS hosted-checkout return URLs (Vietnam billing) */}
                      <Route path="billing/payos/return" element={<PayosReturnPage />} />
                      <Route path="billing/payos/cancel" element={<PayosCancelPage />} />

                      {/* Platform Administration Routes (Super Admin only) */}
                      <Route path="platform">
                        <Route index element={<PlatformDashboard />} />
                        <Route path="mt-engines" element={<MTEnginesManagement />} />
                        <Route path="llm-providers" element={<LLMProvidersManagement />} />
                        <Route path="organizations" element={<OrganizationsManagement />} />
                        <Route path="revenue" element={<RevenueAnalytics />} />
                        <Route path="usage" element={<UsageMonitoring />} />
                        <Route path="costs" element={<PlatformCosts />} />
                      </Route>

                      {/* Settings Routes */}
                      <Route path="settings">
                        <Route 
                          index 
                          element={<Navigate to="/settings/mt-configurations" replace />}
                        />
                        <Route 
                          path="mt-configurations" 
                          element={
                            <ProtectedRoute requiredRole="admin">
                              <MTConfigurations />
                            </ProtectedRoute>
                          } 
                        />
                        <Route
                          path="mt-usage"
                          element={
                            <ProtectedRoute requiredRole="admin">
                              <AdminMTUsageManagement />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="llm-configurations"
                          element={
                            <ProtectedRoute requiredRole="admin">
                              <LLMConfigurations />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="pipeline-admin"
                          element={
                            <ProtectedRoute requiredRole="project_manager">
                              <PipelineAdmin />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="llm-prompt-templates"
                          element={
                            <ProtectedRoute requiredRole="admin">
                              <LLMPromptTemplates />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="auto-approval-rules"
                          element={
                            <ProtectedRoute requiredRole="admin">
                              <AutoApprovalRules />
                            </ProtectedRoute>
                          }
                        />
                        {/* Wave-03 · Semantic TM preview */}
                        <Route
                          path="tm-preview"
                          element={
                            <ProtectedRoute requiredRole="project_manager">
                              <SemanticTMPreview />
                            </ProtectedRoute>
                          }
                        />
                        {/* Wave-04 · Admin domain catalog */}
                        <Route
                          path="domains"
                          element={
                            <ProtectedRoute requiredRole="admin">
                              <DomainsAdmin />
                            </ProtectedRoute>
                          }
                        />
                        {/* Wave-05 · Audit log dashboard */}
                        <Route
                          path="audit-log"
                          element={
                            <ProtectedRoute requiredRole="project_manager">
                              <AuditLogAdmin />
                            </ProtectedRoute>
                          }
                        />
                        {/* Wave-05 · PII scan dashboard */}
                        <Route
                          path="pii-scan"
                          element={
                            <ProtectedRoute requiredRole="project_manager">
                              <PIIScanAdmin />
                            </ProtectedRoute>
                          }
                        />
                        {/* Wave-05 · SSO admin */}
                        <Route
                          path="sso"
                          element={
                            <ProtectedRoute requiredRole="project_manager">
                              <SSOConfigAdmin />
                            </ProtectedRoute>
                          }
                        />
                        {/* Wave-06 · Reviewer capacity dashboard */}
                        <Route
                          path="reviewer-capacity"
                          element={
                            <ProtectedRoute requiredRole="project_manager">
                              <ReviewerCapacityAdmin />
                            </ProtectedRoute>
                          }
                        />
                        {/* Wave-09 · Item 2 · GDPR admin */}
                        <Route
                          path="gdpr"
                          element={
                            <ProtectedRoute requiredRole="admin">
                              <GdprAdmin />
                            </ProtectedRoute>
                          }
                        />
                        {/* Wave-09 · TM leverage weights (memoQ analysis) */}
                        <Route
                          path="tm-leverage-weights"
                          element={
                            <ProtectedRoute requiredRole="admin">
                              <TmLeverageWeightsAdmin />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="format-templates"
                          element={
                            <ProtectedRoute requiredRole="admin">
                              <FormatTemplatesPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="qa-profiles"
                          element={
                            <ProtectedRoute requiredRole="admin">
                              <QAProfiles />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="api-keys"
                          element={
                            <ProtectedRoute requiredRole="admin">
                              <ApiKeysManagement />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="billing"
                          element={
                            <ProtectedRoute requiredRole="admin">
                              <BillingPage />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="branding"
                          element={
                            <ProtectedRoute requiredRole="admin">
                              <BrandingSettings />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="email-templates"
                          element={
                            <ProtectedRoute requiredRole="admin">
                              <EmailTemplatesSettings />
                            </ProtectedRoute>
                          }
                        />
                      </Route>

                      {/* Webhooks Routes (Business tier feature) */}
                      <Route path="webhooks">
                        <Route
                          index
                          element={
                            <ProtectedRoute requiredRole="admin">
                              <WebhookList />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="create"
                          element={
                            <ProtectedRoute requiredRole="admin">
                              <WebhookForm />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path=":id"
                          element={
                            <ProtectedRoute requiredRole="admin">
                              <WebhookDetail />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path=":id/edit"
                          element={
                            <ProtectedRoute requiredRole="admin">
                              <WebhookForm />
                            </ProtectedRoute>
                          }
                        />
                      </Route>

                      {/* Support Tickets Routes */}
                      <Route path="support/tickets">
                        <Route index element={<SupportTicketList />} />
                        <Route path="create" element={<CreateSupportTicket />} />
                        <Route path=":id" element={<SupportTicketDetail />} />
                      </Route>

                    </Route>

                    {/* 404 Fallback */}
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                  </Routes>
                  </Suspense>
                  {/* Wave-07 · Global command palette (Ctrl/Cmd+K) */}
                  <GlobalCommandPalette />
                </div>
              </AuthWrapper> {/* CLOSE: AuthWrapper */}
              </OrganizationProvider>
            </AuthProvider>
          </Router>
          </ErrorBoundary>
        </LocalizationProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;